import { useState } from 'react';
import { Sheet } from '../components/Sheet';
import { useT } from '../i18n/I18nProvider';
import { defaultReasonFor, REASONS } from '../domain/reasons';
import type { PackageInfo, StateId } from '../domain/types';
import { queue, type ReportIssuePayload } from '../api/queueRuntime';
import { FEATURES } from '../api/features';
import type { ReportReasonCode, UserLanguageCode } from '../api/types';

interface Props {
  pkg: PackageInfo | null;
  scannedCode: string;
  stateId: StateId;
  onClose: () => void;
  onSent: () => void;
}

/**
 * Hlášení dispečerům (spec 6).
 *
 * Důvod se předvolí podle stavu zásilky a označí odznakem PODLE STAVU –
 * v typickém případě skladník jen potvrdí a odešle, tedy dva ťuky.
 * U „jiného důvodu" je poznámka povinná, jinak by hlášení nemělo obsah.
 */
export function ReportSheet({ pkg, scannedCode, stateId, onClose, onSent }: Props) {
  const { t, lang } = useT();
  const preselected = defaultReasonFor(stateId);
  const [reason, setReason] = useState<ReportReasonCode | null>(preselected?.id ?? null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  async function send() {
    if (!reason) {
      setError(t('errReason'));
      return;
    }
    if (reason === 'OTHER' && !note.trim()) {
      setError(t('errNote'));
      return;
    }

    setError(null);
    setSending(true);
    try {
      if (FEATURES.reportIssue) {
        const payload: ReportIssuePayload = {
          shipmentId: pkg?.shipmentId ?? '',
          scannedCode,
          statusAtScan: pkg?.statusCode ?? null,
          reason,
          note: note.trim() || null,
          noteLanguage: lang as UserLanguageCode,
          driver: pkg?.pick.driver ?? null,
          routeNumber: pkg?.pick.routeNumber ?? null,
          vehicle: pkg?.pick.vehicleName ?? null,
          scannedAt: new Date().toISOString(),
        };
        // Přes frontu: skladník dostane potvrzení hned, odeslání doběhne
        // na pozadí i při slabém signálu.
        await queue.enqueue('reportIssue', payload);
      }
      onSent();
    } catch (err) {
      console.error('[report] hlášení se nepodařilo zařadit:', err);
      setError(t('sendErr'));
      setSending(false);
    }
  }

  return (
    <Sheet
      title={t('reportTitle')}
      meta={`${pkg?.shipmentNumber ?? scannedCode} · ${t(`s.${stateId}.name` as never)}`}
      onClose={onClose}
    >
      <div className="lbl">{t('reasonLbl')}</div>

      {REASONS.map((r) => (
        <button
          key={r.id}
          type="button"
          className={`reason${reason === r.id ? ' sel' : ''}`}
          aria-pressed={reason === r.id}
          onClick={() => setReason(r.id)}
        >
          <span className="radio" />
          {t(r.key)}
          {preselected?.id === r.id && <span className="pre">{t('byState')}</span>}
        </button>
      ))}

      <div className="lbl" style={{ marginTop: 14 }}>
        {reason === 'OTHER' ? t('noteReq') : t('noteOpt')}
      </div>
      <textarea
        value={note}
        placeholder={t('notePh')}
        onChange={(e) => setNote(e.target.value)}
        aria-label={reason === 'OTHER' ? t('noteReq') : t('noteOpt')}
      />

      {error && <div className="err">{error}</div>}

      <div className="row">
        <button className="btn send" type="button" disabled={sending} onClick={() => void send()}>
          {sending ? t('sending') : t('send')}
        </button>
        <button className="btn ghost" type="button" onClick={onClose}>
          {t('cancel')}
        </button>
      </div>
    </Sheet>
  );
}
